import { v4 as uuid } from 'uuid';
import { normalizeCombos } from '../utils';
import { normalizeVideoSettingsViewCrop } from '../types';
import type {
  NormalizedLibraryProject,
  Segment,
  VideoLearningStatus,
  VideoSettings,
  YouTubeProjectSource
} from '../types';

const DATABASE_NAME = 'croop-guest-library';
const DATABASE_VERSION = 1;
const PROJECT_STORE = 'projects';
const LOCAL_URI_PREFIX = 'indexeddb://croop-guest/';

export interface GuestLibraryRecord {
  id: string;
  source: YouTubeProjectSource | {
    type: 'local-device';
    localUri: string;
    size: number;
  };
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  createdAt: number;
  updatedAt: number;
  videoBlob?: Blob;
  fileName?: string;
  mimeType?: string;
}

const indexedDbUnavailableError = () => new Error(
  'Local library storage is unavailable in this browser. Try a normal browser window or sign in to save to the cloud.'
);

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  if (typeof window === 'undefined' || !window.indexedDB) {
    reject(indexedDbUnavailableError());
    return;
  }

  const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onerror = () => reject(request.error || indexedDbUnavailableError());
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(PROJECT_STORE)) {
      database.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
    }
  };
  request.onsuccess = () => resolve(request.result);
});

const runTransaction = async <T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void
): Promise<T> => {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(PROJECT_STORE, mode);
    const store = transaction.objectStore(PROJECT_STORE);
    let operationCompleted = false;
    let operationResult: T;
    let rejected = false;
    const safeResolve = (value: T) => {
      operationResult = value;
      operationCompleted = true;
    };
    const safeReject = (reason?: unknown) => {
      if (rejected) return;
      rejected = true;
      database.close();
      reject(reason);
    };

    transaction.onabort = () => safeReject(transaction.error || new Error('Local library transaction was aborted.'));
    transaction.onerror = () => safeReject(transaction.error || new Error('Local library transaction failed.'));
    transaction.oncomplete = () => {
      database.close();
      if (rejected) return;
      if (!operationCompleted) {
        safeReject(new Error('Local library transaction completed without a result.'));
        return;
      }
      resolve(operationResult);
    };
    operation(store, safeResolve, safeReject);
  });
};

const putRecord = async (record: GuestLibraryRecord): Promise<void> => runTransaction<void>(
  'readwrite',
  (store, resolve, reject) => {
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }
);

export const getGuestLibraryRecord = async (id: string): Promise<GuestLibraryRecord | null> => runTransaction(
  'readonly',
  (store, resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve((request.result as GuestLibraryRecord | undefined) || null);
    request.onerror = () => reject(request.error);
  }
);

export const listGuestLibraryRecords = async (): Promise<GuestLibraryRecord[]> => runTransaction(
  'readonly',
  (store, resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve((request.result as GuestLibraryRecord[]).sort(
      (first, second) => second.updatedAt - first.updatedAt
    ));
    request.onerror = () => reject(request.error);
  }
);

export const deleteGuestLibraryRecord = async (id: string): Promise<void> => runTransaction<void>(
  'readwrite',
  (store, resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }
);

export const deleteGuestLibraryRecordIfUnchanged = async (
  id: string,
  expectedUpdatedAt: number
): Promise<boolean> => runTransaction<boolean>(
  'readwrite',
  (store, resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onerror = () => reject(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result as GuestLibraryRecord | undefined;
      if (!current) {
        resolve(true);
        return;
      }
      if (current.updatedAt !== expectedUpdatedAt) {
        resolve(false);
        return;
      }
      const deleteRequest = store.delete(id);
      deleteRequest.onsuccess = () => resolve(true);
      deleteRequest.onerror = () => reject(deleteRequest.error);
    };
  }
);

const toProject = (record: GuestLibraryRecord): NormalizedLibraryProject => ({
  id: record.id,
  source: record.source,
  title: record.title,
  segments: normalizeCombos(record.segments || []),
  settings: normalizeVideoSettingsViewCrop(record.settings || {}) as VideoSettings,
  learningStatus: record.learningStatus,
  createdAt: new Date(record.createdAt),
  updatedAt: new Date(record.updatedAt),
  persistence: 'local'
});

const requestPersistentStorage = async () => {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Persistence is best-effort; IndexedDB still works when the browser declines it.
  }
};

export const saveGuestUploadProject = async (
  file: File,
  title: string,
  segments: Segment[],
  settings: VideoSettings
): Promise<NormalizedLibraryProject> => {
  const id = uuid();
  const now = Date.now();
  const record: GuestLibraryRecord = {
    id,
    source: {
      type: 'local-device',
      localUri: `${LOCAL_URI_PREFIX}${id}`,
      size: file.size
    },
    title: title || file.name,
    segments: normalizeCombos(segments),
    settings: normalizeVideoSettingsViewCrop(settings) as VideoSettings,
    createdAt: now,
    updatedAt: now,
    videoBlob: file,
    fileName: file.name,
    mimeType: file.type
  };

  await putRecord(record);
  void requestPersistentStorage();
  return toProject(record);
};

export const saveGuestYouTubeProject = async (
  source: YouTubeProjectSource,
  title: string,
  segments: Segment[],
  settings: VideoSettings
): Promise<NormalizedLibraryProject> => {
  const now = Date.now();
  const record: GuestLibraryRecord = {
    id: uuid(),
    source,
    title: title || source.videoTitle || source.canonicalUrl,
    segments: normalizeCombos(segments),
    settings: normalizeVideoSettingsViewCrop(settings) as VideoSettings,
    createdAt: now,
    updatedAt: now
  };

  await putRecord(record);
  void requestPersistentStorage();
  return toProject(record);
};

export const listGuestLibraryProjects = async (): Promise<NormalizedLibraryProject[]> => (
  (await listGuestLibraryRecords()).map(toProject)
);

export const getGuestLibraryVideoUrl = async (id: string): Promise<string> => {
  const record = await getGuestLibraryRecord(id);
  if (!record?.videoBlob) {
    throw new Error('This locally saved video is no longer available on this device.');
  }
  return URL.createObjectURL(record.videoBlob);
};

export const updateGuestLibraryProject = async (
  id: string,
  updates: Partial<Pick<GuestLibraryRecord, 'title' | 'segments' | 'settings' | 'learningStatus'>>
): Promise<void> => {
  const record = await getGuestLibraryRecord(id);
  if (!record) throw new Error('This local library item could not be found.');

  const next: GuestLibraryRecord = {
    ...record,
    ...updates,
    segments: updates.segments ? normalizeCombos(updates.segments) : record.segments,
    settings: updates.settings
      ? normalizeVideoSettingsViewCrop(updates.settings) as VideoSettings
      : record.settings,
    updatedAt: Date.now()
  };
  if (Object.prototype.hasOwnProperty.call(updates, 'learningStatus') && updates.learningStatus === undefined) {
    delete next.learningStatus;
  }
  await putRecord(next);
};

export const isGuestLibraryProject = (project: NormalizedLibraryProject): boolean => (
  project.persistence === 'local'
);
